import React, { memo, useCallback } from 'react';
import { playButtonSound } from '@wg/web2clientapi/sound';
import { Checkbox, IconTable, TableHead } from '@wg/wows-react-uikit';

import { REQUESTS_TABS } from '~/constants';
import { isChinese } from '~/helpers/realm';

import styles from './Entries.scss';
import TableHeadCell from '../Table/TableHeadCell';

import type { IconTableTypes } from '@wg/wows-react-uikit/components/IconTable/IconTable';

interface EntriesHeaderItem {
  text: string;
  isSortable: boolean;
  name: string;
  modify: string;
  glyph?: string;

  isHidden?: boolean;
  tooltipText?: string;
  tooltipParam?: string;
  sortDefaultIsAsc?: boolean;
}

interface EntriesTableHeadProps {
  entryType: REQUESTS_TABS;
  headers: EntriesHeaderItem[];
  sort: {
    order: string;
    isAsc: boolean;
  };

  allPlayersIsChecked?: boolean | null;
  stickyContainerId: string;
  isActiveSticky?: boolean;

  onAllApplicationsPlayersTick?: (value: boolean) => void;
  onSortClick: (order: string, isAsc: boolean) => void;
}

const EntriesTableHead = ({
  entryType,
  headers,
  sort,
  allPlayersIsChecked,
  onAllApplicationsPlayersTick,
  onSortClick,
}: EntriesTableHeadProps) => {
  const handleHeaderClick = useCallback(
    (e: React.MouseEvent, item: EntriesHeaderItem) => {
      e.preventDefault();

      if (!item.isSortable) {
        return;
      }

      const defaultAsc = item.sortDefaultIsAsc ?? true;
      const isAsc = item.name === sort.order ? !sort.isAsc : defaultAsc;

      onSortClick(item.name, isAsc);
      void playButtonSound();
    },
    [onSortClick, sort.isAsc, sort.order],
  );

  const checkboxCell =
    entryType === REQUESTS_TABS.APPLICATION && onAllApplicationsPlayersTick ? (
      <TableHeadCell modify="check">
        <Checkbox onChange={onAllApplicationsPlayersTick} isChecked={allPlayersIsChecked ?? false} />
      </TableHeadCell>
    ) : null;

  const headersCell = headers.map((item, index) => {
    if (item.isHidden) {
      return null;
    }

    return (
      <TableHeadCell
        key={index}
        modify={item.modify}
        isSortable={item.isSortable}
        isActive={item.isSortable && item.name === sort.order}
        tooltipText={item.tooltipText}
        tooltipParam={!isChinese() ? item.tooltipParam : ''}
        onClick={((e: React.MouseEvent) => handleHeaderClick(e, item)) as () => void}
      >
        {item.glyph ? <IconTable glyph={item.glyph as IconTableTypes} /> : null}
        {!item.glyph || !isChinese() ? item.text : ''}
      </TableHeadCell>
    );
  });

  return (
    <TableHead className={styles.stickyHeader}>
      <>
        {checkboxCell}
        {headersCell}
      </>
    </TableHead>
  );
};

export default memo(EntriesTableHead);
